import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ShieldCheck, AlertTriangle, AlertCircle, FileText, Users, Receipt, Sparkles, RefreshCcw, ChevronDown, ChevronUp } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const STATUS_META = {
  healthy:  { label: 'Healthy',  className: 'text-emerald-700 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-300', ring: 'ring-emerald-300', dial: '#10b981', Icon: ShieldCheck },
  warning:  { label: 'Watch',    className: 'text-amber-700 bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 border-amber-300',         ring: 'ring-amber-300',  dial: '#f59e0b', Icon: AlertCircle },
  critical: { label: 'Critical', className: 'text-rose-700 bg-rose-100 dark:bg-rose-900/30 dark:text-rose-400 border-rose-300',               ring: 'ring-rose-300',   dial: '#ef4444', Icon: AlertTriangle },
};

export default function ComplianceHealthWidget({ cardClass = '' }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [aiOpen, setAiOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiData, setAiData] = useState(null);

  const fetchScore = async () => {
    try {
      const r = await api.get('/compliance/health-score');
      setData(r.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  const fetchAi = async (force = false) => {
    setAiLoading(true);
    try {
      const r = await api.post('/compliance/ai-forecast', { force });
      setAiData(r.data);
      if (!aiOpen) setAiOpen(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = (typeof detail === 'object' && detail?.message) || detail || 'AI forecast failed';
      toast.error(msg);
    } finally {
      setAiLoading(false);
    }
  };

  useEffect(() => { fetchScore(); }, []);

  if (loading) {
    return (
      <Card className={`${cardClass}`} data-testid="widget-compliance-health-loading">
        <CardHeader className="pb-2"><CardTitle className="font-outfit text-base">Compliance Health</CardTitle></CardHeader>
        <CardContent><div className="h-32 animate-pulse bg-muted rounded-md" /></CardContent>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card className={`${cardClass}`} data-testid="widget-compliance-health-error">
        <CardHeader className="pb-2"><CardTitle className="font-outfit text-base">Compliance Health</CardTitle></CardHeader>
        <CardContent><div className="text-sm text-muted-foreground py-6 text-center">Unable to load score</div></CardContent>
      </Card>
    );
  }

  const meta = STATUS_META[data.status] || STATUS_META.healthy;
  const { Icon } = meta;
  const score = Math.max(0, Math.min(100, Number(data.score) || 0));
  const dash = (score / 100) * 339.292;  // 2π × 54
  const cr = data.compliance_reminders || {};
  const hr = data.hr_documents || {};
  const ts = data.tax_submissions || {};

  return (
    <Card className={`${cardClass} overflow-hidden`} data-testid="widget-compliance-health">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <Icon size={16} className={meta.className.split(' ')[0]} />
            Compliance Health
          </CardTitle>
          <Button size="sm" variant="ghost" className="text-xs" onClick={() => navigate('/compliance')} data-testid="compliance-health-view-all">
            Manage
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-5">
          {/* Donut score dial */}
          <div className="relative shrink-0">
            <svg width="120" height="120" viewBox="0 0 120 120" className="-rotate-90">
              <circle cx="60" cy="60" r="54" fill="none" strokeWidth="10" className="stroke-muted" />
              <circle cx="60" cy="60" r="54" fill="none" strokeWidth="10" strokeLinecap="round"
                stroke={meta.dial}
                strokeDasharray={`${dash} 339.292`}
                style={{ transition: 'stroke-dasharray 600ms ease-out' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold font-outfit text-foreground" data-testid="compliance-score">{score}</span>
              <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded mt-0.5 border ${meta.className}`}>{meta.label}</span>
            </div>
          </div>

          {/* Source breakdown */}
          <div className="flex-1 min-w-0 space-y-1.5 text-sm">
            <button onClick={() => navigate('/compliance')} className="w-full flex items-center justify-between p-2 rounded-md hover:bg-muted/50 text-left">
              <span className="flex items-center gap-2 text-foreground"><Receipt size={13} className="text-muted-foreground" /> Compliance Reminders</span>
              <span className="font-mono text-xs text-muted-foreground">
                {cr.overdue ? <span className="text-rose-600 font-bold">{cr.overdue} overdue</span> : null}
                {cr.overdue && cr.due_soon ? <span className="text-muted-foreground"> · </span> : null}
                {cr.due_soon ? <span className="text-amber-600">{cr.due_soon} due</span> : null}
                {!cr.overdue && !cr.due_soon ? <span className="text-emerald-600">{cr.total || 0} OK</span> : null}
              </span>
            </button>
            <button onClick={() => navigate('/hr-documents')} className="w-full flex items-center justify-between p-2 rounded-md hover:bg-muted/50 text-left">
              <span className="flex items-center gap-2 text-foreground"><Users size={13} className="text-muted-foreground" /> HR Documents</span>
              <span className="font-mono text-xs text-muted-foreground">
                {hr.overdue ? <span className="text-rose-600 font-bold">{hr.overdue} overdue</span> : null}
                {hr.overdue && hr.due_soon ? <span className="text-muted-foreground"> · </span> : null}
                {hr.due_soon ? <span className="text-amber-600">{hr.due_soon} due</span> : null}
                {!hr.overdue && !hr.due_soon ? <span className="text-emerald-600">{hr.total || 0} OK</span> : null}
              </span>
            </button>
            <button onClick={() => navigate('/tax-submissions')} className="w-full flex items-center justify-between p-2 rounded-md hover:bg-muted/50 text-left">
              <span className="flex items-center gap-2 text-foreground"><FileText size={13} className="text-muted-foreground" /> Tax Submissions</span>
              <span className="font-mono text-xs text-muted-foreground">
                {ts.rejected_last_90d
                  ? <span className="text-rose-600 font-bold">{ts.rejected_last_90d} rejected (90d)</span>
                  : <span className="text-emerald-600">All accepted</span>}
              </span>
            </button>
          </div>
        </div>

        {/* AI Risk Forecast — collapsible */}
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center justify-between">
            <button
              onClick={() => { setAiOpen(!aiOpen); if (!aiData && !aiLoading && !aiOpen) fetchAi(false); }}
              className="flex items-center gap-2 text-xs font-semibold text-foreground hover:text-violet-600 transition-colors"
              data-testid="toggle-ai-forecast">
              <Sparkles size={13} className="text-violet-500" />
              AI Risk Forecast
              {aiOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            {aiOpen && (
              <button
                onClick={() => fetchAi(true)}
                disabled={aiLoading}
                className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 disabled:opacity-50"
                data-testid="refresh-ai-forecast"
                title="Force refresh (skip 1-hour cache)">
                <RefreshCcw size={10} className={aiLoading ? 'animate-spin' : ''} />
                {aiLoading ? 'Analyzing...' : 'Refresh'}
              </button>
            )}
          </div>

          {aiOpen && (
            <div className="mt-2 p-3 rounded-md bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-950/20 dark:to-indigo-950/20 border border-violet-200/60 dark:border-violet-900/40">
              {aiLoading && !aiData ? (
                <div className="space-y-1.5 animate-pulse">
                  <div className="h-2.5 bg-violet-200/60 dark:bg-violet-800/40 rounded w-full" />
                  <div className="h-2.5 bg-violet-200/60 dark:bg-violet-800/40 rounded w-11/12" />
                  <div className="h-2.5 bg-violet-200/60 dark:bg-violet-800/40 rounded w-3/4" />
                </div>
              ) : aiData ? (
                <>
                  <p className="text-xs leading-relaxed text-foreground" data-testid="ai-forecast-narrative">
                    {aiData.narrative}
                  </p>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-violet-200/40 dark:border-violet-800/30">
                    <p className="text-[10px] text-muted-foreground">
                      {aiData.items_in_horizon} item(s) in 90-day horizon · {aiData.velocity_180d || 0} renewal(s) in last 180d
                    </p>
                    <p className="text-[10px] text-muted-foreground font-mono">
                      {aiData.from_cache ? `cached · ${Math.round((aiData.age_seconds || 0) / 60)}m old` : aiData.model || 'live'}
                    </p>
                  </div>
                </>
              ) : (
                <div className="text-xs text-muted-foreground text-center py-2">
                  Click "Refresh" to generate an AI risk forecast
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
